import { type Ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { isShowPrizePool as _isShowPrizePool, getEntriesDisplay, getLeagueNamesDisplay } from '@/core/fantasySelection/contestDisplayHelper';
import { formatToServerTime } from '@/core/lib/date';
import { i18n } from '@/core/lib/i18n';
import type { BadgeColor } from '@/interface/IBadge';
import type { IBet } from '@/interface/IFantasySelectionMyBets';
import { ContestStatus, FantasySelectionBetStatus, RoutePage } from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';

export function useMyBets(bet: Ref<IBet>) {
    const leagueNames = computed(() => getLeagueNamesDisplay(bet.value.leagues.map(league => league.name)));

    const isShowCheckDetail = computed(() => ![FantasySelectionBetStatus.Refund, FantasySelectionBetStatus.Amended, FantasySelectionBetStatus.Reversed].includes(bet.value.status));

    const { formatMoneyWithSymbolAndDecimal, formatMoneyWithDecimal } = useCustomerStore();
    const stakeDisplay = computed(() => formatMoneyWithSymbolAndDecimal(bet.value.stake));
    const prizePoolDisplay = computed(() => formatMoneyWithSymbolAndDecimal(bet.value.pool.prizePool));

    const winLostDisplayWithSymbol = computed(() => formatMoneyWithSymbolAndDecimal(bet.value.winLost ?? 0));
    const winLostDisplay = computed(() => formatMoneyWithDecimal(bet.value.winLost ?? 0));
    const playerCommissionDisplay = computed(() => formatMoneyWithDecimal(bet.value.playerCommission));

    const betDate = computed(() => formatToServerTime(bet.value.betTime, 'MM/dd/yyyy'));
    const betTime = computed(() => formatToServerTime(bet.value.betTime, 'HH:mm:ss'));

    const statusColorMapping: Partial<Record<FantasySelectionBetStatus, BadgeColor>> = {
        [FantasySelectionBetStatus.Running]: 'running',
        [FantasySelectionBetStatus.Won]: 'won',
        [FantasySelectionBetStatus.Lose]: 'lose',
        [FantasySelectionBetStatus.Refund]: 'refunded',
        [FantasySelectionBetStatus.Amended]: 'blue',
        [FantasySelectionBetStatus.Reversed]: 'blue',
    };
    const status = computed(() => {
        const statusMapping: Partial<Record<FantasySelectionBetStatus, string>> = {
            [FantasySelectionBetStatus.Running]: '7',
            [FantasySelectionBetStatus.Won]: '11',
            [FantasySelectionBetStatus.Lose]: '4',
            [FantasySelectionBetStatus.Refund]: '5',
            [FantasySelectionBetStatus.Amended]: '1',
            [FantasySelectionBetStatus.Reversed]: '2',
        };
        return (statusMapping[bet.value.status] ? i18n.t(`bet_status_${statusMapping[bet.value.status]}`) : '');
    });

    const isShowRanking = computed(() => bet.value.status === FantasySelectionBetStatus.Won || bet.value.status === FantasySelectionBetStatus.Lose);
    const isShowPrizePool = computed(() => _isShowPrizePool(bet.value.status));
    const isShowWinLost = computed(() => bet.value.status === FantasySelectionBetStatus.Won
    || bet.value.status === FantasySelectionBetStatus.Lose
    || bet.value.status === FantasySelectionBetStatus.Refund);

    const isShowEntries = computed(() => bet.value.status === FantasySelectionBetStatus.Refund || bet.value.status === FantasySelectionBetStatus.Running);
    const entriesDisplay = computed(() => getEntriesDisplay(bet.value.pool.maxEntries, bet.value.pool.currentEntries));

    const checkDetailText = computed(() => {
        if (bet.value.contest.status === ContestStatus.Running) {
            return i18n.t('fantasy_selection_check_contest_info');
        }
        return i18n.t('fantasy_selection_check_rankings');
    });

    const router = useRouter();
    function handleCheckDetail() {
        if (bet.value.contest.status === ContestStatus.Running) {
            router.push({
                name: RoutePage.FantasySelectionContestPools,
                params: {
                    contestId: bet.value.contest.id,
                },
            });
        }

        if (bet.value.contest.status === ContestStatus.PendingResult || bet.value.contest.status === ContestStatus.Completed) {
            router.push({
                name: RoutePage.FantasySelectionMyBetDetail,
                params: {
                    refNo: bet.value.refNo,
                },
            });
        }
    }

    return {
        leagueNames,
        isShowCheckDetail,
        stakeDisplay,
        prizePoolDisplay,
        winLostDisplay,
        winLostDisplayWithSymbol,
        playerCommissionDisplay,
        betDate,
        betTime,
        statusColorMapping,
        status,
        isShowRanking,
        isShowWinLost,
        isShowPrizePool,
        isShowEntries,
        entriesDisplay,
        checkDetailText,
        handleCheckDetail,
    };
}
